/* eslint-disable react-refresh/only-export-components */
import { useCallback, useEffect, useMemo, useState } from "react";
import { fetchAuthSession } from "./api";
import { getPrivyEmbeddedConfig } from "./privyEmbeddedConfig.js";
import { hasPrivyEmbeddedAuthResume } from "./privyEmbeddedAuthResume.js";
import { hasPrivyOAuthCallbackParams } from "./privyOAuthUrlCleanup.js";
import { defaultWalletAuthValue, WalletAuthContext, useWalletAuth } from "./walletAuthContext";
import { shouldAutoLoadWalletRuntime } from "./walletRuntimeAutoload.js";

export { useWalletAuth };

function readWalletRuntimeTimeoutMs() {
  const value = typeof window === "undefined" ? 0 : Number(window.__PM_TEST_WALLET_RUNTIME_TIMEOUT_MS__);
  return Number.isFinite(value) && value > 0 ? value : 10_000;
}

function withTimeout(promise, timeoutMs) {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = window.setTimeout(() => reject(new Error("Wallet support timed out.")), timeoutMs);
  });

  return Promise.race([promise, timeout]).finally(() => window.clearTimeout(timeoutId));
}

export function WalletAuthProvider({ children }) {
  const [runtimeBridge, setRuntimeBridge] = useState(null);
  const [runtimeStatus, setRuntimeStatus] = useState("idle");
  const [walletAuthValue, setWalletAuthValue] = useState(defaultWalletAuthValue);
  const [session, setSession] = useState(null);
  const [loadingSession, setLoadingSession] = useState(true);
  const [runtimeErrorMessage, setRuntimeErrorMessage] = useState("");
  const WalletAuthRuntimeBridge = runtimeBridge;

  const refreshSession = useCallback(async () => {
    const active = await fetchAuthSession();
    setSession(active);
    return active;
  }, []);

  useEffect(() => {
    let active = true;

    setLoadingSession(true);
    refreshSession()
      .catch(() => {
        if (active) {
          setSession(null);
        }
      })
      .finally(() => {
        if (active) {
          setLoadingSession(false);
        }
      });

    return () => {
      active = false;
    };
  }, [refreshSession]);

  const loadWalletRuntime = useCallback(async () => {
    if (runtimeBridge || runtimeStatus === "loading") {
      return;
    }

    setRuntimeStatus("loading");
    setRuntimeErrorMessage("");
    try {
      const module = await withTimeout(import("./walletAuthRuntime"), readWalletRuntimeTimeoutMs());
      setRuntimeBridge(() => module.WalletAuthRuntimeBridge);
      setRuntimeStatus("loaded");
    } catch {
      setRuntimeStatus("error");
      setRuntimeErrorMessage("Wallet support could not load. Refresh and try again.");
    }
  }, [runtimeBridge, runtimeStatus]);

  useEffect(() => {
    const privyEmbeddedEnabled = getPrivyEmbeddedConfig().enabled;
    if (!shouldAutoLoadWalletRuntime({
      loadingSession,
      runtimeLoaded: Boolean(runtimeBridge),
      runtimeStatus,
      session,
      privyEmbeddedEnabled,
      hasPrivyAuthResume: privyEmbeddedEnabled ? hasPrivyEmbeddedAuthResume() : false,
      hasPrivyOAuthCallback: privyEmbeddedEnabled ? hasPrivyOAuthCallbackParams() : false
    })) {
      return;
    }

    loadWalletRuntime().catch(() => null);
  }, [loadingSession, loadWalletRuntime, runtimeBridge, runtimeStatus, session]);

  const fallbackValue = useMemo(
    () => ({
      ...defaultWalletAuthValue,
      isAuthenticated: Boolean(session?.walletAddress || session?.userId),
      canLogout: Boolean(session?.walletAddress || session?.userId),
      loadingSession,
      refreshSession,
      session,
      walletCanSignTransaction: false,
      walletSupportMessage: runtimeErrorMessage
    }),
    [loadingSession, refreshSession, runtimeErrorMessage, session]
  );
  const baseValue = WalletAuthRuntimeBridge ? walletAuthValue : fallbackValue;
  const value = useMemo(
    () => ({
      ...baseValue,
      loadWalletRuntime,
      walletRuntimeLoaded: Boolean(WalletAuthRuntimeBridge),
      walletRuntimeLoading: runtimeStatus === "loading"
    }),
    [WalletAuthRuntimeBridge, baseValue, loadWalletRuntime, runtimeStatus]
  );

  return (
    <WalletAuthContext.Provider value={value}>
      {WalletAuthRuntimeBridge ? <WalletAuthRuntimeBridge onValueChange={setWalletAuthValue} initialSession={session} /> : null}
      {children}
    </WalletAuthContext.Provider>
  );
}
